import { useState, useEffect, useRef } from 'react'
import * as m from 'framer-motion/m'
import { Container } from './Container'
import { ResponsiveImage } from './ResponsiveImage'

const STOCK_VIDEO = '/assets/stock.mp4'

const iconClass = 'w-4 h-4 sm:w-5 sm:h-5'

const BarChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={iconClass}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
)

const BrainIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={iconClass}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.5 2A2.5 2.5 0 0112 4.5v15a2.5 2.5 0 01-4.96.44 2.5 2.5 0 01-2.96-3.08 3 3 0 01-.34-5.58 2.5 2.5 0 011.32-4.24 2.5 2.5 0 011.98-3A2.5 2.5 0 019.5 2z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 2A2.5 2.5 0 0012 4.5v15a2.5 2.5 0 004.96.44 2.5 2.5 0 002.96-3.08 3 3 0 00.34-5.58 2.5 2.5 0 00-1.32-4.24 2.5 2.5 0 00-1.98-3A2.5 2.5 0 0014.5 2z" />
  </svg>
)

const LightbulbIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={iconClass}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
  </svg>
)

const ITEMS = [
  { title: 'Disciplined analysis', desc: 'Detailed financial modelling, scenario testing, and sensitivity analysis', Icon: BarChartIcon },
  { title: 'Market intelligence', desc: 'Informed by policy trends, capital flows, and sector fundamentals', Icon: BrainIcon },
  { title: 'Structured creativity', desc: 'Designing bespoke capital solutions that balance opportunity with risk', Icon: LightbulbIcon },
] as const

export function OurApproach() {
  const [stockVideoInView, setStockVideoInView] = useState(false)
  const stockVideoRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = stockVideoRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) setStockVideoInView(true)
      },
      { rootMargin: '100px', threshold: 0 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <section className="bg-[#1e232b] border-t border-white/10" id="approach">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start py-12 md:py-20">
          <m.div className="grid grid-cols-2 gap-3 sm:gap-6 pt-6 md:pt-[60px] order-2 md:order-1" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <div ref={stockVideoRef} className="relative w-full rounded overflow-hidden bg-[#1e232b]" style={{ paddingTop: '150%' }}>
              {stockVideoInView ? (
                <video
                  className="absolute inset-0 w-full h-full object-cover"
                  title="Architecture and design"
                  loop
                  muted
                  autoPlay
                  playsInline
                  preload="metadata"
                  disablePictureInPicture
                  disableRemotePlayback
                  aria-label="Architecture and design"
                  src={STOCK_VIDEO}
                />
              ) : (
                <div className="absolute inset-0 w-full h-full bg-[#1e232b]" aria-hidden />
              )}
            </div>
            <ResponsiveImage name="AdobeStock_33203909-scaled" alt="Building and development" responsive loading="lazy" fallbackExt="jpeg" className="w-full h-auto rounded" sizes="(max-width: 768px) 45vw, 22vw" width={400} height={600} />
          </m.div>
          <m.div className="pt-0 md:pt-[6vw] order-1 md:order-2" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 className="uppercase text-[#cab6a1] text-[28px] sm:text-[36px] md:text-[48px] leading-[1.2em] font-serif">Our Approach</h2>
            <div className="w-[50px] sm:w-[65px] my-3 sm:my-4">
              <ResponsiveImage name="attorney-1" alt="Decorative icon for Our Approach" loading="lazy" fallbackExt="png" className="w-full h-auto" width={65} height={65} />
            </div>
            <div className="text-white/90 leading-[1.8em] text-sm sm:text-[16px] font-sans">
              <p>We operate with the precision of an investor and the perspective of an advisor.</p>
              <p className="mt-4">Our process is defined by:</p>
            </div>
            <div className="mt-6 space-y-6">
              {ITEMS.map((item, i) => {
                const Icon = item.Icon
                return (
                <m.div key={item.title} className="flex items-start gap-3" initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}>
                  <span className="shrink-0 inline-flex items-center justify-center rounded-full bg-[#cab6a1] text-[#1e232b] w-7 h-7 sm:w-8 sm:h-8" aria-hidden>
                    <Icon />
                  </span>
                  <div className="min-w-0">
                    <h3 className="uppercase text-white text-sm sm:text-[16px] md:text-[20px] font-medium font-sans">{item.title}</h3>
                    <p className="text-white/85 leading-[1.8em] text-sm sm:text-[15px] font-sans">{item.desc}</p>
                  </div>
                </m.div>
                )
              })}
            </div>
          </m.div>
        </div>
      </Container>
    </section>
  )
}
